// Derives the InsightsView the AI Insights agent reads — "what is on this
// report's screen right now" — from the report's live state.
//
// Pure by design: every context value the derivation needs is passed in, so the
// interesting logic (per-gadget filter/date inheritance, which groupings the
// agent can and cannot reproduce, the reading-order gadget cap) is unit-testable
// without mounting a report. RegisterReportInsightsView is the thin component
// that reads the contexts and calls this.

import describeComparison from 'formatters/describeComparison';
import cardTypeCheckers from 'types/cardTypeCheckers';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';
import visTypeCheckers from 'types/visTypeCheckers';

import {
  InsightsView,
  InsightsViewGadget,
  viewFilterClauses,
} from '../../contextProviders/InsightsContentProvider/viewTypes';
import {
  buildDimensions,
  datasetsForMetric,
  describeSeriesOverrides,
  describeTrend,
  MAX_VIEW_FILTERS,
  MAX_VIEW_GADGETS,
  toClause,
  toViewFilter,
} from '../InsightsChat/insightsViewGadget';

// Re-exported so existing importers (mergeAgentFiltersIntoDrillDowns, the report
// builder tests) keep resolving them from here after the move to the shared
// insightsViewGadget module.
export { toClause, MAX_VIEW_GADGETS, MAX_VIEW_FILTERS };

export interface ReportInsightsViewInput {
  cards: CanvasCard.Card[];
  reportGadgetsLookup: {
    [id: string]: ReportGadgets.ReportGadget | undefined;
  };
  // Already resolved to concrete dates by useDateScope.
  dateScope: DateRangeInput;
  scopeAsFixed: FixedFilter[];
  drillDownsAsFixed: FixedFilter[];
  baseViews: { [dataset: string]: FleetOps.BaseView | undefined };
  metricOptionsLookup: { [metricId: string]: Metrics.Metric | undefined };
  defaultDatasetFiltersLookup: {
    [dataset: string]: DefaultDatasetFilter | undefined;
  };
  comparison?: PersistedComparisonType;
  weekStartsOn?: WeekStartsOn;
  isReportDefaultFiltersDisabled: boolean;
  // From useGetMetricIdsFromVis — reused so every gadget type resolves its
  // metrics the same way the rest of the app does.
  getMetricIds: (vis?: ExtendedVisualisationDefinition) => string[];
}

// A gadget's own scope plates that are already resolved to fixed filters. Plates
// referencing a variable or saved filter can't be resolved without the same
// machinery FilterPlatesProvider uses, so they're skipped here — the report-level
// filters (which ARE resolved) carry the bulk of the meaning.
const gadgetFixedFilters = (
  gadget: ReportGadgets.ReportGadget,
): FixedFilter[] =>
  (gadget.scope || [])
    .filter(filterPlateTypeCheckers.isFixed)
    .map((plate) => plate.fixedValue);

const buildReportInsightsView = ({
  cards,
  reportGadgetsLookup,
  dateScope,
  scopeAsFixed,
  drillDownsAsFixed,
  baseViews,
  metricOptionsLookup,
  defaultDatasetFiltersLookup,
  comparison,
  weekStartsOn,
  isReportDefaultFiltersDisabled,
  getMetricIds,
}: ReportInsightsViewInput): InsightsView => {
  const allFilters = [...scopeAsFixed, ...drillDownsAsFixed].map((filter) =>
    toViewFilter({ filter, baseViews }),
  );
  const filters = allFilters.slice(0, MAX_VIEW_FILTERS);

  // Reading order — top to bottom, then left to right. This is what the user is
  // actually looking at, which is what makes truncating at MAX_VIEW_GADGETS
  // defensible rather than arbitrary.
  const gadgetCards = cards
    .filter(cardTypeCheckers.isReportGadget)
    .slice()
    .sort((a, b) =>
      a.layout.y === b.layout.y
        ? a.layout.x - b.layout.x
        : a.layout.y - b.layout.y,
    );

  let hasUnreproducibleFilters = allFilters.some(
    (f) => viewFilterClauses(f).length === 0,
  );

  const allGadgets: InsightsViewGadget[] = [];
  gadgetCards.forEach((card) => {
    const gadget = reportGadgetsLookup[card.content.reportGadgetId];
    // A gadget saved without a chart (e.g. an abandoned add) draws nothing, so
    // there's nothing on screen to describe
    if (!gadget || !gadget.chartDef) {
      return;
    }
    const { chartDef } = gadget;

    const metricIds = [...new Set(getMetricIds(chartDef))];
    const metrics = metricIds.map((id) => {
      const name = metricOptionsLookup[id]?.name;
      return { id, ...(name ? { name } : {}) };
    });

    // Default dataset filters are applied invisibly per-gadget and have no
    // aggregate_metric equivalent, so their presence alone means a recomputed
    // figure may legitimately differ from the screen.
    const defaultFiltersDisabled = (() => {
      const mode = gadget.defaultDatasetFiltersMode;
      if (mode === 'enabled') {
        return false;
      }
      if (mode === 'disabled') {
        return true;
      }
      return gadget.isDefaultFiltersDisabled
        ? true
        : isReportDefaultFiltersDisabled;
    })();
    if (!defaultFiltersDisabled) {
      const hasDefaultFilter = metricIds
        .flatMap((id) =>
          datasetsForMetric({ metricId: id, metricOptionsLookup }),
        )
        .some((dataset) => !!defaultDatasetFiltersLookup[dataset]);
      if (hasDefaultFilter) {
        hasUnreproducibleFilters = true;
      }
    }

    const extraFilters = gadgetFixedFilters(gadget).map((filter) =>
      toViewFilter({ filter, baseViews }),
    );
    if (extraFilters.some((f) => viewFilterClauses(f).length === 0)) {
      hasUnreproducibleFilters = true;
    }

    const isV5 = visTypeCheckers.isV5ChartDef(chartDef);
    const trend = isV5 ? describeTrend(chartDef) : {};
    if (trend.isTrendReproducible === false) {
      hasUnreproducibleFilters = true;
    }

    const dimensions = buildDimensions({ chartDef, baseViews });

    // Series asking their own question, which a re-query must ask too
    const seriesOverrides = describeSeriesOverrides({
      chartDef,
      metricOptionsLookup,
      baseViews,
    });
    if (
      seriesOverrides.some((series) =>
        (series.filters ?? []).some((f) => viewFilterClauses(f).length === 0),
      )
    ) {
      hasUnreproducibleFilters = true;
    }

    allGadgets.push({
      // The card id, not the gadget id: the values registry is keyed by card, so
      // the same gadget placed twice reports two sets of numbers.
      cardId: card.layout.i,
      title: chartDef.name,
      metrics,
      ...dimensions,
      ...trend,
      ...(isV5 && chartDef.groupByLimit !== undefined
        ? { groupByLimit: chartDef.groupByLimit }
        : {}),
      ...(isV5 && chartDef.groupBySortBy
        ? { groupBySort: chartDef.groupBySortBy }
        : {}),
      // NOTE: gadget.dateRange / relativeDateRange / advancedRelativeDateRange
      // are deliberately ignored — ReportGadgetDateScopeProvider never reads
      // them (the range always comes from the report), so reporting them would
      // make this context lie about what's on screen.
      ...(gadget.dateField ? { dateFieldOverride: gadget.dateField } : {}),
      ...(extraFilters.length ? { extraFilters } : {}),
      ...(gadget.isDefaultFiltersDisabled
        ? { ignoresReportFilters: true }
        : {}),
      ...(gadget.comparison
        ? { comparison: describeComparison(gadget.comparison) }
        : {}),
      ...(seriesOverrides.length > 0 ? { seriesOverrides } : {}),
    });
  });

  const gadgets = allGadgets.slice(0, MAX_VIEW_GADGETS);

  return {
    ...(dateScope.startDate ? { startDate: dateScope.startDate } : {}),
    ...(dateScope.endDate ? { endDate: dateScope.endDate } : {}),
    ...(dateScope.dateField ? { dateField: dateScope.dateField } : {}),
    ...(describeComparison(comparison)
      ? { comparison: describeComparison(comparison) }
      : {}),
    filters,
    ...(allFilters.length > filters.length
      ? { filtersTruncatedFrom: allFilters.length }
      : {}),
    hasUnreproducibleFilters,
    gadgets,
    ...(allGadgets.length > gadgets.length
      ? { gadgetsTruncatedFrom: allGadgets.length }
      : {}),
    ...(weekStartsOn ? { weekStartsOn } : {}),
  };
};

export default buildReportInsightsView;
